'use client';

import { useState, useEffect } from 'react';
import { Box, Grid, TextField, CircularProgress, Button, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import FormDialog from '../../../../components/FormDialog';
import PickerField from '../../../../components/PickerField';
import { useNotifier } from '../../../../components/Notifier';
import { useLanguage } from '../../../../utils/i18n';
import { createTireBrand, updateTireBrand, getTireBrandById } from '../../../../stores/tireBrandStore';
import { useCountryList } from '../../../../stores/countryStore';
import { useManufactureDataList } from '../../../../stores/manufactureStore';
import { useCompanyDataList } from '../../../../stores/companyStore';
import { formatUpperCase } from '../../../../utils/formatTitleCase';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import ManufactureAddEdit from '../manufacture/ManufactureAddEdit';
import MasterCountryAddEdit from '../../../master-data/country/MasterCountryAddEdit';

const getInitials = (name) => {
  if (!name) return '';
  const cleaned = name.replace(/PT\.?|TBK\.?|CV\.?/gi, '').trim();
  const words = cleaned.split(' ').filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  const consonants = cleaned.replace(/[aeiouAEIOU\s]/g, '');
  if (consonants.length >= 3) return consonants.substring(0, 3).toUpperCase();
  return cleaned.substring(0, 3).toUpperCase();
};

const MasterBrandAddEdit = ({ open, id = null, onClose, onSuccess }) => {
  const notify = useNotifier();
  const { t } = useLanguage();

  const [initialValues, setInitialValues] = useState({
    company_id: typeof window !== 'undefined' ? localStorage.getItem('companyId') : '',
    code: '',
    name: '',
    manufacture_id: '',
    country_id: '',
    status: 'Active'
  });

  const [loading, setLoading] = useState(false);

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const countryPickerParams = usePickerPagination({
    fetchHook: useCountryList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  const manufacturePickerParams = usePickerPagination({
    fetchHook: useManufactureDataList,
    buildArgs: (state) => [state.page, state.pageSize, [], state.searchKey, state.searchValue],
    searchKey: 'name'
  });

  useEffect(() => {
    if (!open) return;

    let mounted = true;
    const fetch = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getTireBrandById(id);
        if (!mounted) return;
        if (data) {
          setInitialValues((prev) => ({
            ...prev,
            code: data.code ?? prev.code,
            name: data.name ?? prev.name,
            manufacture_id: data.manufacture ?? data.manufacture_id ?? prev.manufacture_id,
            country_id: data.country ?? data.country_id ?? prev.country_id,
            status: data.status
          }));
        }
      } catch (err) {
        console.error('Failed to load brand:', err);
        notify({ message: t('brand.failedLoadData', 'Failed to load brand data'), severity: 'error' });
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [id, open, notify, t]);

  const validationSchema = Yup.object().shape({
    company_id: Yup.string().required(t('general.validationCompanyRequired', 'Company is required')),
    code: Yup.string().required(t('brand.validationCodeRequired', 'Brand Code is required')),
    name: Yup.string().required(t('brand.validationNameRequired', 'Brand Name is required')),
    manufacture_id: Yup.string().required(t('brand.validationManufactureRequired', 'Manufacture is required')),
    country_id: Yup.string().required(t('brand.validationOriginRequired', 'Country is required')),
    status: Yup.string().required(t('general.validationStatusRequired', 'Status is required'))
  });

  const handleClose = () => {
    formik.resetForm();
    if (onClose) onClose();
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const payload = {
          company: Number(values.company_id),
          code_internal: values.code,
          name: formatUpperCase(values.name) ?? '',
          manufacture: Number(values.manufacture_id),
          country: Number(values.country_id),
          status: values.status
        };

        if (id) {
          await updateTireBrand(id, payload);
          notify({ message: t('brand.updateSuccess', 'Brand updated successfully'), severity: 'success' });
        } else {
          await createTireBrand(payload);
          notify({ message: t('brand.createSuccess', 'Brand added successfully'), severity: 'success' });
        }

        formik.resetForm();
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.message || 'Failed to save data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    const brandName = formik.values.name;
    const mfgId = formik.values.manufacture_id;

    let mfgInitials = '';
    if (mfgId) {
      const mfgOpt = manufacturePickerParams.options.find((o) => String(o.value) === String(mfgId));
      if (mfgOpt) {
        mfgInitials = getInitials(mfgOpt.label);
      }
    }

    let brandPart = '';
    if (brandName) {
      brandPart = brandName.replace(/\s+/g, '').toUpperCase();
      if (brandPart.length > 3 && !brandPart.endsWith('001')) {
        brandPart += '001';
      }
    }

    if (mfgInitials && brandPart) {
      formik.setFieldValue('code', `${mfgInitials}-${brandPart}`);
    } else if (brandPart) {
      formik.setFieldValue('code', `${brandPart}`);
    } else {
      formik.setFieldValue('code', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.name, formik.values.manufacture_id, manufacturePickerParams.options]);

  const dialogActions = (
    <>
      <Button onClick={handleClose} disabled={formik.isSubmitting} sx={{ color: 'text.secondary', textTransform: 'none' }}>
        {t('buttonTitle.cancel', 'Cancel')}
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={formik.handleSubmit}
        disabled={formik.isSubmitting || loading}
        startIcon={formik.isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
        sx={{ textTransform: 'none', borderRadius: 1, px: 3, fontWeight: 700 }}
      >
        {formik.isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
      </Button>
    </>
  );

  return (
    <FormDialog
      open={open}
      onClose={handleClose}
      title={id ? t('general.edit', 'Edit') + ' ' + t('brand.title', 'Brand') : t('general.add', 'Add') + ' ' + t('brand.title', 'Brand')}
      maxWidth="sm"
      fullScreenMode={false}
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', pt: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <form onSubmit={formik.handleSubmit}>
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12 }}>
                <PickerField
                  label={t('general.company', 'Company') + ' *'}
                  name="company_id"
                  value={formik.values.company_id}
                  onChange={(val) => formik.setFieldValue('company_id', val)}
                  onBlur={() => formik.setFieldTouched('company_id', true)}
                  error={formik.touched.company_id && Boolean(formik.errors.company_id)}
                  helperText={formik.touched.company_id && formik.errors.company_id}
                  {...companyPickerParams}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('brand.name', 'Brand Name') + ' *'}
                  name="name"
                  value={formik.values.name}
                  onChange={(e) => {
                    formik.setFieldValue('name', formatUpperCase(e.target.value));
                  }}
                  onBlur={formik.handleBlur}
                  size="medium"
                  fullWidth
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <PickerField
                  label={t('brand.manufacture', 'Manufacture') + ' *'}
                  name="manufacture_id"
                  allowAdd
                  AddComponent={ManufactureAddEdit}
                  value={formik.values.manufacture_id}
                  onChange={(val) => formik.setFieldValue('manufacture_id', val)}
                  onBlur={() => formik.setFieldTouched('manufacture_id', true)}
                  error={formik.touched.manufacture_id && Boolean(formik.errors.manufacture_id)}
                  helperText={formik.touched.manufacture_id && formik.errors.manufacture_id}
                  {...manufacturePickerParams}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  label={t('brand.code', 'Brand Code') + ' *'}
                  name="code"
                  value={formik.values.code}
                  size="medium"
                  fullWidth
                  disabled
                  error={formik.touched.code && Boolean(formik.errors.code)}
                  helperText={formik.touched.code && formik.errors.code}
                  slotProps={{
                    input: {
                      readOnly: true
                    }
                  }}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <PickerField
                  label={t('brand.country', 'Country') + ' *'}
                  name="country_id"
                  allowAdd
                  AddComponent={MasterCountryAddEdit}
                  value={formik.values.country_id}
                  onChange={(val) => formik.setFieldValue('country_id', val)}
                  onBlur={() => formik.setFieldTouched('country_id', true)}
                  error={formik.touched.country_id && Boolean(formik.errors.country_id)}
                  helperText={formik.touched.country_id && formik.errors.country_id}
                  {...countryPickerParams}
                />
              </Grid>

              <Grid item size={{ xs: 12 }}>
                <TextField
                  select
                  label={t('masterCompany.status', 'Status') + ' *'}
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  fullWidth
                  error={formik.touched.status && Boolean(formik.errors.status)}
                  helperText={formik.touched.status && formik.errors.status}
                >
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </form>
        )}
      </Box>
    </FormDialog>
  );
};

export default MasterBrandAddEdit;
